"use client";
import { use, useEffect, useCallback, useRef, useState } from "react";
import { useParams, usePathname } from "next/navigation";
import { parseFiltersFromSearchParams } from "@/app/utils/filters.utils";
import type { CampaignDetailData } from "@/app/lib/features/campaign/campaign.types";
import useCampaign from "@/app/lib/features/campaign/campaign.hook";
import useFilters from "@/app/lib/features/filters/filters.hook";
import { trackCampaignView } from "@/app/lib/tracking/interactions";
import useFilterUrlSync from "@/app/hooks/useFilterUrlSync";
import useDebounce from "@/app/hooks/useDebounce";
import {
  DEFAULT_FILTER_REQUEST,
  FilterRequest,
} from "@/app/lib/features/filters/filters.types";
import DynamicBreadcrumb from "@/app/components/elements/breadcrumb/DynamicBreadcrumb";
import ProductsLayout from "@/app/components/common/products/ProductsLayout";
import ProductsHeader from "@/app/components/common/products/ProductsHeader";
import GenericProductsPageSkeleton from "@/app/components/elements/skeletons/GenericProductsPageSkeleton";
import NotFoundPage from "@/app/(pages)/not-found/NotFoundPage";
import Image from "next/image";
import { CalendarDays, Tag, Ticket } from "lucide-react";

const BASE_FILTER_REQUEST: FilterRequest = {
  section_id: DEFAULT_FILTER_REQUEST.section_id,
  seque: DEFAULT_FILTER_REQUEST.seque,
};

interface CampaignDetailClientProps {
  initialDataPromise: Promise<CampaignDetailData | null>;
}

const CampaignDetailClient = ({
  initialDataPromise,
}: CampaignDetailClientProps) => {
  const params = useParams();
  const code = params.code as string;
  const initialData = use(initialDataPromise);

  // SSR verisi filtresiz base listedir (ISR cache'i query'den bağımsız).
  // URL'de filtre varsa kullanılmaz; hydration mismatch olmasın diye ilk client
  // render'ı sunucuyla aynı kalır (hydrated bayrağı).
  const [hasUrlFiltersOnMount] = useState(() => {
    if (typeof window === "undefined") return false;
    return (
      Object.keys(
        parseFiltersFromSearchParams(
          new URLSearchParams(window.location.search),
        ),
      ).length > 0
    );
  });
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => setHydrated(true), []);

  const canUseSsrData = !!initialData && (!hydrated || !hasUrlFiltersOnMount);
  // İlk client fetch'i atla: SSR zaten aynı isteği yaptı (yalnızca filtresiz açılışta).
  const skipFirstFetchRef = useRef(!!initialData && !hasUrlFiltersOnMount);

  const {
    campaign,
    items,
    pagination,
    isCampaignLoading,
    isCampaignSuccess,
    isCampaignError,
    hasMoreCampaignItems,
    fetchCampaign,
    fetchMoreCampaignItems,
    clearCampaign,
    seedInitialCampaign,
  } = useCampaign();

  const {
    selected,
    updateSelected,
    fetchFilters,
    fetchGeneralFilters,
    reset: resetFilters,
    clearFilters,
    userInteractionCount,
  } = useFilters();

  const pathname = usePathname();
  const handleClearFilters = useCallback(() => {
    clearFilters();
    setTimeout(() => {
      window.history.replaceState(null, "", pathname);
    }, 0);
  }, [clearFilters, pathname]);

  const { activeFilterRequest, initReady } = useFilterUrlSync({
    selected,
    updateSelected,
    baseFilterRequest: BASE_FILTER_REQUEST,
    interactionCount: userInteractionCount,
  });

  const debouncedKey = useDebounce(JSON.stringify(activeFilterRequest), 500);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { section_id: _sid, seque: _seq, ...campaignFilters } = activeFilterRequest;

  // SSR verisini Redux'a yükle (mount'ta bir kez) — fetch effect'i bunu
  // skipFirstFetchRef ile atlıyor, çift istek olmuyor.
  useEffect(() => {
    if (!initialData || hasUrlFiltersOnMount) return;
    seedInitialCampaign(initialData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!initReady || !code) return;
    fetchFilters(activeFilterRequest);
    fetchGeneralFilters({});
    if (skipFirstFetchRef.current) {
      // İlk açılış SSR verisiyle karşılandı; filtre değişince buraya tekrar gelinir.
      skipFirstFetchRef.current = false;
      return;
    }
    fetchCampaign(code, campaignFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedKey, code, initReady]);

  useEffect(() => {
    if (campaign?.id) trackCampaignView(campaign.id);
  }, [campaign?.id]);

  useEffect(() => {
    return () => {
      clearCampaign();
      resetFilters();
    };
  }, [clearCampaign, resetFilters]);

  if (isCampaignError) {
    return (
      <NotFoundPage
        title="Kampanya Bulunamadı"
        description="Aradığınız kampanya mevcut değil veya süresi dolmuş olabilir."
        icon={Ticket}
        primaryLabel="Tüm Kampanyalar"
        primaryHref="/kampanyalar"
        showHomeButton
      />
    );
  }

  // Redux boşken (SSR + hydration arası) doğrudan ISR verisinden render edilir.
  const displayCampaign = campaign ?? (canUseSsrData ? initialData!.campaign : null);
  const displayItems =
    items.length > 0 ? items : canUseSsrData ? initialData!.items : [];
  const total =
    pagination?.total ??
    (canUseSsrData ? initialData!.pagination?.total : undefined) ??
    displayItems.length;
  const campaignTitle = displayCampaign?.title ?? "";
  const hasContent = isCampaignSuccess || displayItems.length > 0;

  if (isCampaignLoading && displayItems.length === 0) {
    return <GenericProductsPageSkeleton />;
  }

  return (
    <div className="container-main">
      <DynamicBreadcrumb title={campaignTitle || "Kampanya"} />

      {displayCampaign && (
        <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-4 mb-4 flex flex-col sm:flex-row sm:items-center gap-4">
          {displayCampaign.image ? (
            <div className="relative w-14 h-14 shrink-0">
              <Image
                src={displayCampaign.image}
                alt={displayCampaign.title}
                fill
                className="object-cover rounded-lg border border-gray-200"
                sizes="56px"
              />
            </div>
          ) : (
            <div className="w-14 h-14 shrink-0 rounded-lg bg-black flex items-center justify-center">
              <Ticket size={24} className="text-white" />
            </div>
          )}

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              {/* h2: aynı başlık ProductsHeader'da h1 olarak basılıyor —
                  SSR'a açıldıktan sonra sayfada iki h1 oluşuyordu. */}
              <h2 className="font-semibold text-base leading-tight">
                {displayCampaign.title}
              </h2>
              {displayCampaign.amount_formatted && displayCampaign.amount_formatted !== "0₺" && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-medium">
                  <Tag size={11} />
                  {displayCampaign.amount_formatted}
                </span>
              )}
            </div>
            {displayCampaign.sub_title && (
              <p className="text-sm text-muted-foreground mt-0.5">
                {displayCampaign.sub_title}
              </p>
            )}
            <div className="mt-1.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-white border border-dashed border-gray-300 text-xs text-gray-500 font-mono tracking-wide">
              <Ticket size={11} />
              {displayCampaign.code}
            </div>
          </div>

          {displayCampaign.end_date_text && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground shrink-0 bg-white border border-gray-200 rounded-lg px-3 py-2">
              <CalendarDays size={13} />
              <span>{displayCampaign.end_date_text}</span>
            </div>
          )}
        </div>
      )}

      <ProductsLayout
        header={(onOpenFilter) => (
          <ProductsHeader
            title={campaignTitle}
            productCount={hasContent && total > 0 ? total : undefined}
            onOpenFilter={onOpenFilter}
          />
        )}
        products={displayItems}
        hasMore={hasMoreCampaignItems}
        isLoading={isCampaignLoading}
        isSuccess={hasContent}
        onLoadMore={fetchMoreCampaignItems}
        onClearFilters={handleClearFilters}
      />
    </div>
  );
};

export default CampaignDetailClient;